<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const { t } = useI18n();
const currentAccountId = useMapGetter('getCurrentAccountId');
const isSaving = ref(false);

const allowProductDiscountPerItem = computed(() =>
  store.getters['accounts/allowProductDiscountPerItem'](currentAccountId.value)
);

const maxDiscountPercent = computed(() =>
  store.getters['accounts/maxDiscountPercent'](currentAccountId.value)
);

const showOutOfStockProducts = computed(() =>
  store.getters['accounts/showOutOfStockProducts'](currentAccountId.value)
);

const maxDiscountInput = ref(maxDiscountPercent.value ?? '');

watch(maxDiscountPercent, value => {
  maxDiscountInput.value = value ?? '';
});

const updateAllowProductDiscount = async value => {
  isSaving.value = true;
  try {
    await store.dispatch('accounts/update', {
      allow_product_discount_per_item: value,
      options: { silent: true },
    });
    useAlert(t('PDV.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PDV.SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const updateMaxDiscount = async () => {
  isSaving.value = true;
  const val =
    maxDiscountInput.value === '' ? null : Number(maxDiscountInput.value);
  try {
    await store.dispatch('accounts/update', {
      max_discount_percent: val,
      options: { silent: true },
    });
    useAlert(t('PDV.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PDV.SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const updateShowOutOfStockProducts = async value => {
  isSaving.value = true;
  try {
    await store.dispatch('accounts/update', {
      show_out_of_stock_products: value,
      options: { silent: true },
    });
    useAlert(t('PDV.SETTINGS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('PDV.SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex-1 overflow-y-auto p-4 md:p-6 bg-n-background">
    <div class="max-w-3xl mx-auto space-y-6">
      <div>
        <h3 class="text-base font-semibold text-n-text">
          {{ $t('PDV.SETTINGS.SECTIONS.SALES.TITLE') }}
        </h3>
        <p class="text-sm text-n-text-subtle mt-1">
          {{ $t('PDV.SETTINGS.SECTIONS.SALES.DESCRIPTION') }}
        </p>
      </div>

      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 divide-y divide-n-weak"
      >
        <div class="flex items-center justify-between gap-3 px-4 py-3">
          <div>
            <p class="text-sm font-medium text-n-text">
              {{ $t('PDV.SETTINGS.ALLOW_PRODUCT_DISCOUNT.TITLE') }}
            </p>
            <p class="text-xs text-n-text-subtle mt-0.5">
              {{ $t('PDV.SETTINGS.ALLOW_PRODUCT_DISCOUNT.DESCRIPTION') }}
            </p>
          </div>
          <ToggleSwitch
            :model-value="allowProductDiscountPerItem"
            :disabled="isSaving"
            @update:model-value="updateAllowProductDiscount"
          />
        </div>

        <div class="flex items-center justify-between gap-3 px-4 py-3">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-text">
              {{ $t('PDV.SETTINGS.MAX_DISCOUNT_PERCENT.TITLE') }}
            </p>
            <p class="text-xs text-n-text-subtle mt-0.5">
              {{ $t('PDV.SETTINGS.MAX_DISCOUNT_PERCENT.DESCRIPTION') }}
            </p>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <Input
              v-model="maxDiscountInput"
              type="number"
              min="0"
              max="100"
              :placeholder="$t('PDV.SETTINGS.MAX_DISCOUNT_PERCENT.PLACEHOLDER')"
              class="w-24"
              :disabled="isSaving"
              @blur="updateMaxDiscount"
            />
            <span class="text-sm text-n-text-subtle">%</span>
          </div>
        </div>

        <div class="flex items-center justify-between gap-3 px-4 py-3">
          <div>
            <p class="text-sm font-medium text-n-text">
              {{ $t('PDV.SETTINGS.SHOW_OUT_OF_STOCK_PRODUCTS.TITLE') }}
            </p>
            <p class="text-xs text-n-text-subtle mt-0.5">
              {{ $t('PDV.SETTINGS.SHOW_OUT_OF_STOCK_PRODUCTS.DESCRIPTION') }}
            </p>
          </div>
          <ToggleSwitch
            :model-value="showOutOfStockProducts"
            :disabled="isSaving"
            @update:model-value="updateShowOutOfStockProducts"
          />
        </div>
      </div>
    </div>
  </div>
</template>
